import { Box, Drawer, Grid } from "@mui/material";
import { makeStyles } from "../../AppContainer/mui-theme";
import classnames from "classnames";
import { find, get, includes, isEmpty, map, pick } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { getParams, navigateTo, setParams } from "../../../utils/location";
import { getAppMedia, mediaTypes } from "../../../utils/media";
import AppContainer from "../../AppContainer";
import BackgroundImage from "../../BackgroundImage";
import BackgroundImageExtrnalLink from "../../BackgroundImageExtrnalLink";
import BrandFooter from "../../BrandFooter";
import Button from "../../Button";
import HomeTitle from "../../HomeTitle";
import InfoIcon from "../../icons/Info.svg";
import LearnMoreIcon from "../../icons/LearnMore.svg";
import TableIcon from "../../icons/Table.svg";
import LABELS from "../../labels";
import Promotion from "../../promotion";
import PromotionsAvatars from "../../promotion/promotions-avatars";
import HomeButtons from "../HomeButtons";
import * as styles from "./index.module.scss";
import {isNewsfeedTemplateLayout} from "../../../logic/templates/isNewsfeedTemplateLayout";
import ReorderWidget from "../../Widget/reorder/reorder";
import { useSelector } from "react-redux";
import { getLatestOrders, getCashbackDiscounts } from "../../../store/selectors";
import FeaturedWidget from "../../Widget/featured/featured";
import Gallery from "../../Widget/gallery";
import SubscribeWidget from "../../Widget/subscribe";
import NewRewardToast from "../../NewRewardToast";
import CallToActionButtons from "./CallToActionButtons";
import TitleComponent from "./Title";
import { isTemplateEnabled } from "../../../logic/templates/isTemplateEnabled";
import { HOMEPAGE_TEMPLATE_KEY } from "../../../utils/constants";

export default function DefaultTemplate({
  startNewOrder,
  order,
  T,
  appStyles,
  promotions,
  widgets,
  subscriptionPolicies,
  pageContext,
  user,
  location,
  openAuthView,
  app,
  openNewOrPrevOrderModal
}) {
  const { servingOptions, branches, businessAppConfiguration, pointBankCoupons, business } = pageContext;
  const { classes } = useStyles();
  const [openDesktopPromotions, setOpenDesktopPromotions] = useState(false);
  const handleOpenPromotions = () => setOpenDesktopPromotions(true);
  const handleClosePromotions = () => setOpenDesktopPromotions(false);

  const { orderItems, servingOptionType, branchId } = order;
  
  const latestOrders = useSelector((state) => getLatestOrders(state, { pageContext }));
  const cashbackDiscounts = useSelector((state) => getCashbackDiscounts(state, { pageContext }));

  const isSimulateNewRewardActive =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("simulateNewReward");

  const widgetsList = useMemo(
    () => appStyles.widgets ? appStyles.widgets.reduce((acc, el) => {
        if (el.type === "promotion") {
          const promotion = find(
            promotions,
            ({ id }) => id === el.referenceWidgetId
          );
          if (promotion){
            acc.push({ ...el, ...promotion });
          }
        } else {
          const widget = find(
            widgets,
            ({ id }) => id === el.referenceWidgetId
          );

          // don't show reorder widget if no prev order
          if (widget?.type === 'reorder' && isEmpty(latestOrders)) {
            return acc;
          }

          if (widget?.type === "subscribe" && isEmpty(subscriptionPolicies)) {
            return acc;
          }

          if (widget){
            acc.push(widget);
          }
        }
        return acc;
      }, []) : appStyles.homepagePromotions ? appStyles.homepagePromotions.reduce((acc, el) => {
        const promotion = find(
          promotions,
          ({ id }) => id === el
        );
        if (promotion){
          acc.push({...promotion, type: "promotion"});
        }
        return acc;
      }, []) : [],
    [promotions, widgets, appStyles.widgets, subscriptionPolicies]
  );

  const shouldShowNewRewardToast =
    isSimulateNewRewardActive || user.loggedIn;

  const galleryWidget = find(widgetsList, (widget) => widget.type === "gallery");

  const navigateToTargetOrLoginMobile = useCallback(
    ({ type, target }) => () => {
      user.loggedIn ? navigateTo(target) : openAuthView(type);
    },
    [openAuthView, user.loggedIn],
  );

  const navigateToTargetOrLoginDesktop = useCallback(
    ({ type, target }) => () => {
      user.loggedIn ? navigateTo(target) : openAuthView(type);
    },
    [openAuthView, user.loggedIn],
  );
  const skipServingOptionsPage = get(appStyles, "skipServingOptionsPage", false);
  const shouldChooseServingOptionFromModal = get(appStyles, "shouldChooseServingOptionFromModal", false);

  const navigateToPath = skipServingOptionsPage || servingOptions.length < 2 || shouldChooseServingOptionFromModal ? "/find-location" : "/serving-options";
  const navigateToNewOrder = useCallback(
    () =>
      navigateTo(
        navigateToPath
      ),
    [],
  );

  const handleStartNewOrderClicked = useCallback(
    (e) => {
      e.preventDefault();
      const isDuringOrder = !isEmpty(orderItems);
      if (isDuringOrder) {
        openNewOrPrevOrderModal({ context: 'homepage' });
      } else {
        startNewOrder();
        navigateToNewOrder();
      }
    },
    [orderItems],
  );

  const disableRewards =
    get(businessAppConfiguration, "disableRewards") &&
    !appStyles.rewardsAlternative;
  const enableFeedback = get(businessAppConfiguration, "enableFeedback");
  const requireLoginForGiftCard = get(
    businessAppConfiguration,
    "requireLoginForGiftCard",
  );
  const giftCardsEnabled = get(businessAppConfiguration, "giftCardsEnabled");

  const showGiftCard = giftCardsEnabled;

  const showRestaurantInteractionBlock = get(appStyles, "homepageRestaurantInteractionBar", true);

  const { links = {} } = appStyles;

  const split = appStyles.homepageImageSplit;
  const tiles = !!appStyles.homepageTileButtons;
  const webAppBackground =
    getAppMedia(mediaTypes.webAppBackground, appStyles) || {};

  const webMobileAppBackground =
    getAppMedia(mediaTypes.webMobileAppBackground, appStyles) ||
    webAppBackground ||
    {};

  const isSSR = typeof window === "undefined";

  const isNative = !isSSR && window.isNativeApp;

  const disableReorder = includes(appStyles.disabledFeatures, "reorder");
  const disableOrder = includes(appStyles.disabledFeatures, "order");
  const hasTwoButtons = disableRewards || (disableReorder && disableOrder);
  const newsfeedTemplateIsSelected = isNewsfeedTemplateLayout(appStyles);
  const isLoyaltyTemplate = isTemplateEnabled(appStyles, HOMEPAGE_TEMPLATE_KEY.loyalty);

  const backgroundImageHeight = getBackgroundImageHeight({
    split,
    tiles,
    isNative,
    hasTwoButtons,
    newsfeedTemplateIsSelected,
    appStyles
  });


  const showMobilePromotions =
    newsfeedTemplateIsSelected && (!isEmpty(promotions) || !isEmpty(widgetsList));

  const showDesktopPromotions =
    newsfeedTemplateIsSelected && !app.isMobile && !isEmpty(promotions);
  
  const { homepageShowTiersList, homepageHidePoints } = appStyles;

  const GalleryLayout = (({
    galleryWidget,
    appStyles,
    pageContext,
    actionButtonsProps,
    titleProps,
  }) => (
    <div style={{ marginBottom: "20px" }}>
      <div style={{ position: "relative", minHeight: "100vw", overflow: "hidden" }}>
        <Gallery
          appStyles={appStyles}
          pageContext={pageContext}
          widget={galleryWidget}
        />
        {isLoyaltyTemplate && titleProps ? (
          <div className={styles.GalleryLoyaltyOverlay}>
            <TitleComponent {...titleProps} />
            <CallToActionButtons {...actionButtonsProps} className={styles.GalleryWidget} />
          </div>
        ) : (
          <CallToActionButtons {...actionButtonsProps} className={styles.GalleryWidget} />
        )}
      </div>
    </div>
  ));

  const BackgroundImageLayout = (({ 
    webMobileAppBackground,
    backgroundImageHeight,
    split,
    homepageHidePoints,
    homepageShowTiersList,
    titleProps,
    actionButtonsProps
  }) => {
    const backgroundImageStyle = {
      ...(!homepageHidePoints && homepageShowTiersList && {
        height: "fit-content",
        position: "relative",
        minHeight: backgroundImageHeight
      })
    };

    return (
      <BackgroundImage
        noAnimation
        mediaType={mediaTypes.webMobileAppBackground}
        mediaKey={webMobileAppBackground.imageKey}
        sizes="768px"
        imagePreview={webMobileAppBackground.imagePreview}
        classNames={styles.BackgroundImage}
        split={split}
        height={backgroundImageHeight}
        style={backgroundImageStyle}
      >
        <TitleComponent {...titleProps} />
        <CallToActionButtons {...actionButtonsProps} />
      </BackgroundImage>
    );
  });

  const BackgroundComponent = () => {
    const shouldShowGallery = galleryWidget;
    
    const actionButtonsProps = {
      appStyles,
      split,
      showRestaurantInteractionBlock,
      enableFeedback,
      showGiftCard,
      T,
      homepageShowTiersList,
    };

    const titleProps = {
      T,
      appStyles,
      user,
      pointBankCoupons,
      homepageImageSplit: appStyles.homepageImageSplit,
      enableFeedback,
      business,
      isMobile: app.isMobile,
      businessAppConfiguration,
      cashbackDiscounts,
    };

    if (shouldShowGallery) {
      return (
        <>
          <div className={styles.HideOnDesktop}>
            {GalleryLayout({
              galleryWidget,
              appStyles,
              pageContext,
              actionButtonsProps,
              titleProps,
            })}
          </div>
          <div className={styles.HideOnMobile}>
            {BackgroundImageLayout({
              webMobileAppBackground,
              backgroundImageHeight,
              split,
              homepageHidePoints,
              homepageShowTiersList,
              titleProps,
              actionButtonsProps,
            })}
          </div>
        </>
      );
    }

    return BackgroundImageLayout({
      webMobileAppBackground,
      backgroundImageHeight,
      split,
      homepageHidePoints,
      homepageShowTiersList,
      titleProps,
      actionButtonsProps,
    });
  };

  return (
    <>
      {shouldShowNewRewardToast && (
        <NewRewardToast appStyles={appStyles} T={T} />
      )}
      <AppContainer.Content appStyles={appStyles} classNames={styles.Content}>
        <HomeTitle
          T={T}
          title={T("{{desktop_title_prefix}}")}
          desktopExtraTitle={T("{{desktop_extra_title}}")}
          appStyles={appStyles}
          forDesktop={true}
          className={classnames(!split && enableFeedback && styles.HomeTitle && styles.HideOnMobile)}
        />

        {BackgroundComponent()}

        <HomeButtons
          T={T}
          showMobilePromotions={showMobilePromotions}
          appStyles={appStyles}
          loggedIn={user.loggedIn}
          handleStartNewOrderClicked={handleStartNewOrderClicked}
          isMobile={app.isMobile}
          disableRewards={disableRewards}
          amountOfGiftsUnseen={user.amountOfGiftsUnseen}
          navigateToTargetOrLoginDesktop={navigateToTargetOrLoginDesktop}
          navigateToTargetOrLoginMobile={navigateToTargetOrLoginMobile}
        />

        {showMobilePromotions && (
          <Box padding={2} className={styles.HideOnDesktop}>
            <Grid container direction="column" spacing={2}>
              {map(widgetsList, (widget, indx) => {
                return widget.type !== "gallery" && (
                  <Grid item key={indx}>
                    {widget.type === "promotion" && (
                      <Promotion
                        promotion={widget}
                        appStyles={appStyles}
                        navigateToTargetOrLoginDesktop={
                          navigateToTargetOrLoginDesktop
                        }
                        navigateToTargetOrLoginMobile={
                          navigateToTargetOrLoginMobile
                        }
                        handleStartNewOrderClicked={handleStartNewOrderClicked}
                        T={T}
                      />
                    )}
                    {widget.type === "reorder" && (
                      <ReorderWidget
                        appStyles={appStyles}
                        pageContext={pageContext}
                        widget={widget}
                      />
                    )}
                    {widget.type === "featured" && (
                      <FeaturedWidget
                        appStyles={appStyles}
                        pageContext={pageContext}
                        widget={widget}
                      />
                    )}
                    {widget.type === "subscribe" && (
                      <SubscribeWidget
                        appStyles={appStyles}
                        pageContext={pageContext}
                        widget={widget}
                        subscriptionPolicies={subscriptionPolicies}
                        navigateToTargetOrLoginMobile={navigateToTargetOrLoginMobile}
                        T={T}
                      />
                    )}
                  </Grid>
              );
              })}
            </Grid>
          </Box>
        )}
      </AppContainer.Content>
      <AppContainer.SecondaryContent relative>
        <AppContainer.AttachedContent
          classNames={classnames(
            styles.TopSideButtons,
            appStyles.rtl && styles.RTL
          )}
        >
          {showRestaurantInteractionBlock && (
            <>
              {enableFeedback &&
                (appStyles.contactUsAlternative?.href ? (
                  <BackgroundImageExtrnalLink
                    appStyles={appStyles}
                    slim
                    to={appStyles.contactUsAlternative.href}
                  >
                    {T(
                      appStyles.contactUsAlternative.label ||
                        LABELS.CONTACT_US
                    )}
                  </BackgroundImageExtrnalLink>
                ) : (
                  <Button
                    appStyles={appStyles}
                    to="/contact-us"
                    slim
                    centered
                  >
                    {T(LABELS.CONTACT_US)}
                  </Button>
                ))}

              {showGiftCard && (
                <Button
                  appStyles={appStyles}
                  to={setParams("/gift-card", { onCloseTarget: "/" })}
                  centered
                  slim
                >
                  {T("Send a Gift Card")}
                </Button>
              )}
            </>
          )}

          {showDesktopPromotions && (
            <PromotionsAvatars
              open={openDesktopPromotions}
              promotions={widgetsList}
              onOpen={handleOpenPromotions}
              onClose={handleClosePromotions}
            />
          )}

          {showDesktopPromotions && (
            <Drawer
              className={classes.drawer}
              variant="persistent"
              classes={{
                paper: classes.drawerPaper,
              }}
              anchor="right"
              open={openDesktopPromotions}
            >
              <Box padding={2}>
                <Grid container direction="column" spacing={2}>
                  {map(widgetsList, (widget = {}, indx) => {
                    const { type } = widget;
                    return (
                      <Grid item key={indx}>
                        {type === "promotion" && (
                          <Promotion
                            promotion={widget}
                            appStyles={appStyles}
                            navigateToTargetOrLoginDesktop={
                              navigateToTargetOrLoginDesktop
                            }
                            navigateToTargetOrLoginMobile={
                              navigateToTargetOrLoginMobile
                            }
                            handleStartNewOrderClicked={
                              handleStartNewOrderClicked
                            }
                            T={T}
                          />
                        )}  
                        {type === "reorder" && (
                          <ReorderWidget
                            appStyles={appStyles}
                            pageContext={pageContext}
                            widget={widget}
                          />
                        )}
                        {type === "featured" && (
                          <FeaturedWidget
                            appStyles={appStyles}
                            pageContext={pageContext}
                            widget={widget}
                          />
                        )}
                        {type === "subscribe" && (
                          <SubscribeWidget
                            appStyles={appStyles}
                            pageContext={pageContext}
                            widget={widget}
                            subscriptionPolicies={subscriptionPolicies}
                            navigateToTargetOrLoginMobile={navigateToTargetOrLoginDesktop}
                            T={T}
                          />
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Drawer>
          )}
        </AppContainer.AttachedContent>

        {links.nutrition && (
          <BackgroundImageExtrnalLink
            appStyles={appStyles}
            to={links.nutrition}
          >
            <InfoIcon />
            {T("See our nutrition facts")}
          </BackgroundImageExtrnalLink>
        )}
        {appStyles.scanAlternative && (
          <BackgroundImageExtrnalLink
            appStyles={appStyles}
            to={appStyles.scanAlternative.href}
            bottom
            classNames={styles.ScanAlternative}
          >
            {appStyles.scanAlternative.useLearnMoreIcon ? (
              <LearnMoreIcon style={{ width: 37, height: "auto" }} />
            ) : (
              <TableIcon />
            )}{" "}
            {T(appStyles.scanAlternative.label)}
          </BackgroundImageExtrnalLink>
        )}
        {map(appStyles.externalLinks, (link, indx) => (
          <BackgroundImageExtrnalLink
            key={link + indx}
            appStyles={appStyles}
            bottom
            to={link.href}
            classNames={styles.ScanAlternative}
          >
            {link.useLearnMoreIcon ? <LearnMoreIcon /> : <TableIcon />}
            {T(link.label)}
          </BackgroundImageExtrnalLink>
        ))}
        <BackgroundImage
          mediaType={mediaTypes.webAppBackground}
          mediaKey={webAppBackground.imageKey}
          sizes="1920px"
          imagePreview={webAppBackground.imagePreview}
          noAnimation
        />
      </AppContainer.SecondaryContent>
      <BrandFooter
        T={T}
        appStyles={appStyles}
        backPath="/"
        params={getParams(location)}
      />
    </>
  );
}

const getBackgroundImageHeight = ({
  split,
  tiles,
  isNative,
  hasTwoButtons,
  newsfeedTemplateIsSelected,
  appStyles,
}) => {
  const { homepageShowTiersList, homepageHidePoints } = appStyles;

  if (appStyles && appStyles.mobileBackgroundImageHeight){
    return appStyles.mobileBackgroundImageHeight;
  }
  if (newsfeedTemplateIsSelected) {
    return "100vw";
  }
  if (!split) {
    return "calc(100% - 32px)";
  }
  if (split && tiles) {
    if (isNative) {
      return "calc(100% - 2 * (50vw - 2 * 32px) - 60px -  60px)";
    } else {
      return "calc(100% - 2 * (50vw - 2 * 32px) - 60px - 60px - 32px - 16px - 10px)";
    }
  }

  if (isNative) {
    return `calc(100% - 212px - 60px - 12px - 32px${
      hasTwoButtons ? " + 72px" : ""
    })`;
  } else {
    return `calc(100% - 212px - 60px - 12px - 60px - 20px${
      hasTwoButtons ? " + 72px" : ""
    })`;
  }
};

const drawerWidth = 376;

const useStyles = makeStyles()((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    position: "absolute",
    top: 120,
    height: `calc(100vh - 260px)`,
    borderLeft: "none",
    width: drawerWidth,
    backgroundColor: "transparent",
    WebkitMaskImage:
      "-webkit-gradient(linear, right 96%, right bottom, from(rgba(0,0,0,1)), to(rgba(0,0,0,0)))",
  },
}));
